import { type ColumnDef } from "@tanstack/react-table";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTableColumnHeader } from "@/components/data-tables/data-table-column-header";
import { formatDate, formatRelativeTime } from "@/lib/utils";
import { UserGroupActions } from "./UserGroupActions";
import { z } from "zod";
import { userGroupValidators } from "@shared/validators/admin/user-groups-validators";
import i18n from "@/utils/i18n/i18n";

type UserGroup = z.infer<typeof userGroupValidators.userGroupSchema>;

interface UserGroupManagementActions {
    onViewDetails?: (userGroup: UserGroup) => void;
    onEdit?: (userGroup: UserGroup) => void;
    onManageUsers?: (userGroup: UserGroup) => void;
    onManageConnections?: (userGroup: UserGroup) => void;
    onDelete?: (userGroup: UserGroup) => void;
}

export function createUserGroupColumns(actions: UserGroupManagementActions): ColumnDef<UserGroup>[] {
    return [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label={i18n.t("columns.selectAll")}
                    className="translate-y-[2px]"
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label={i18n.t("columns.selectRow")}
                    className="translate-y-[2px]"
                />
            ),
            enableSorting: false,
            enableHiding: false,
            size: 40,
        },
        {
            accessorKey: "name",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.groupName")} />,
            cell: ({ row }) => {
                return (
                    <div className="flex space-x-2">
                        <span className="max-w-32 truncate font-medium sm:max-w-48 md:max-w-72 lg:max-w-[31rem]">
                            {row.getValue("name")}
                        </span>
                    </div>
                );
            },
            size: 200,
        },
        {
            accessorKey: "description",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.description")} />,
            cell: ({ row }) => {
                return (
                    <div className="flex space-x-2">
                        <span className="max-w-32 truncate sm:max-w-48 md:max-w-72 lg:max-w-[31rem]">
                            {row.getValue("description")}
                        </span>
                    </div>
                );
            },
            // Hide on mobile
            meta: {
                className: "hidden md:table-cell",
            },
        },
        {
            accessorKey: "externalId",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.objectId")} />,
            cell: ({ row }) => {
                const externalId = row.getValue("externalId") as string | null;
                const isDefault = (row.original as any).isDefault === true;
                
                if (externalId) {
                    return <span className="truncate max-w-32 block">{externalId}</span>;
                }
                
                return (
                    <span className="text-xs text-muted-foreground italic">
                        {isDefault ? i18n.t("columns.default") : i18n.t("columns.manual")}
                    </span>
                );
            },
            size: 120,
            // Hide on small screens
            meta: {
                className: "hidden lg:table-cell",
            },
        },
        {
            accessorKey: "createdAt",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.created")} />,
            cell: ({ row }) => <span className="whitespace-nowrap">{formatDate(row.getValue("createdAt"))}</span>,
            size: 120,
            // Hide on mobile and tablet
            meta: {
                className: "hidden xl:table-cell",
            },
        },
        {
            accessorKey: "updatedAt",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.lastUpdated")} />,
            cell: ({ row }) => <span className="whitespace-nowrap">{formatRelativeTime(row.getValue("updatedAt"))}</span>,
            size: 130,
            // Hide on mobile
            meta: {
                className: "hidden sm:table-cell",
            },
        },
        {
            id: "actions",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.actions")} className="text-end" />,
            cell: ({ row }) => (
                <div className="flex justify-end">
                    <UserGroupActions
                        userGroup={row.original}
                        onEdit={actions.onEdit}
                        onManageUsers={actions.onManageUsers}
                        onManageConnections={actions.onManageConnections}
                        onDelete={actions.onDelete}
                    />
                </div>
            ),
            enableSorting: false,
            enableHiding: false,
            size: 80,
        },
    ];
}

export const UserGroupSearchableColumns = [
    {
        id: "name" as keyof UserGroup,
        get title() { return i18n.t("columns.groupName"); },
    },
    {
        id: "description" as keyof UserGroup,
        get title() { return i18n.t("columns.description"); },
    },
];
